import React, { useCallback } from "react";
import { ImpersonationManager } from "./ImpersonationManager";
import {
  AuthenticationManager,
  useRawJwt,
  useLogout,
  useSessionProvider,
} from "./AuthenticationManager";
import { GetAuthorizationsAsync } from "./types";
import { TokenRenewalManager } from "./TokenRenewalManager";
import { SessionExpiryGuard } from "./SessionExpiryGuard";

type AuthProps = {
  getAuthorizations: GetAuthorizationsAsync;
};

/**
 * Everything that may issue an authenticated request lives inside the expiry
 * guard, including the renewal manager's prompts. When the token expires the
 * guard signs the user out and stops rendering this subtree, which unmounts the
 * background pollers below it and clears their intervals.
 */
const AuthenticatedSession: React.FC = ({ children }) => {
  const jwt = useRawJwt();
  const logout = useLogout();
  const provider = useSessionProvider();

  const onExpired = useCallback(() => logout(false), [logout]);

  // Unreachable in practice: AuthenticationManagerDisplay renders this subtree
  // only when isAuthenticated, which requires a user holding an id_token. Fail
  // closed anyway — this component is a wrapper, so rendering children without a
  // token would mount the authenticated tree, and its background pollers, with
  // no expiry guard around them.
  if (!jwt) {
    return null;
  }

  // The guard never depends on knowing the provider — only renewal does. A
  // session whose provider is unknown (a cross-tab broadcast whose payload
  // omits it, as from an older build during a rolling deploy) still expires on
  // schedule; it simply cannot be renewed silently, and the guard ends it
  // rather than letting it run unguarded.
  return (
    <SessionExpiryGuard jwt={jwt} onExpired={onExpired}>
      {children}
      {provider && (
        <TokenRenewalManager jwt={jwt} provider={provider} logout={logout} />
      )}
    </SessionExpiryGuard>
  );
};

const Auth: React.FC<AuthProps> = ({ getAuthorizations, children }) => {
  return (
    <AuthenticationManager getAuthorizations={getAuthorizations}>
      <AuthenticatedSession>
        <ImpersonationManager getAuthorizations={getAuthorizations}>
          {children}
        </ImpersonationManager>
      </AuthenticatedSession>
    </AuthenticationManager>
  );
};

export default Auth;
